// TFVF Admin Content — lets admins edit module title/summary/duration/
// required/audience/content chunks. Each save bumps the version and
// leaves the previous version snapshot immutable (see backend).
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save, Plus, Trash2, FileText } from 'lucide-react';
import { API, authHeaders } from './tfvfShared';
import { useAuth } from '../../../contexts/AuthContext';

const TfvfAdmin = () => {
  const { token } = useAuth();
  const [modules, setModules] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [mod, setMod] = useState(null);
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    Promise.all([
      axios.get(`${API}/tfvf/modules`, { headers: authHeaders(token) }),
      axios.get(`${API}/tfvf/audit`, { headers: authHeaders(token) }).catch(() => ({ data: { rows: [] } })),
    ]).then(([m, a]) => {
      const list = m.data?.modules || [];
      setModules(list);
      setSelectedId(list[0]?.module_id || '');
      setAudit(a.data?.rows || []);
    }).finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!selectedId || !token) return;
    axios.get(`${API}/tfvf/modules/${selectedId}`, { headers: authHeaders(token) })
      .then(r => setMod({
        ...r.data,
        content_chunks: r.data.content_chunks || [],
      }));
  }, [selectedId, token]);

  const save = async () => {
    if (!mod) return;
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/tfvf/admin/modules/${selectedId}`,
        {
          title: mod.title,
          summary: mod.summary,
          duration_minutes: Number(mod.duration_minutes),
          audience: mod.audience,
          required: !!mod.required,
          content_chunks: mod.content_chunks,
        },
        { headers: authHeaders(token) },
      );
      toast.success(`Saved as v${r.data.version}`);
      setMod(r.data);
      // Refresh audit
      const a = await axios.get(`${API}/tfvf/audit`, { headers: authHeaders(token) });
      setAudit(a.data?.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const patchChunk = (idx, patch) => {
    setMod(m => ({
      ...m,
      content_chunks: m.content_chunks.map((c, i) => i === idx ? { ...c, ...patch } : c),
    }));
  };
  const addChunk = () => setMod(m => ({ ...m, content_chunks: [...m.content_chunks, { title: 'New section', body_markdown: '', source_doc: 'admin_edit' }] }));
  const removeChunk = (idx) => setMod(m => ({ ...m, content_chunks: m.content_chunks.filter((_, i) => i !== idx) }));

  const filteredAudit = useMemo(
    () => audit.filter(r => r.module_id === selectedId).slice(0, 10),
    [audit, selectedId],
  );

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--tf-forest)]/50" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-5" data-testid="tfvf-admin-page">
      {/* Sidebar */}
      <aside className="lg:col-span-1 space-y-1">
        <p className="tf-eyebrow mb-2">Modules</p>
        {modules.map(m => (
          <button
            key={m.module_id}
            onClick={() => setSelectedId(m.module_id)}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm ${
              selectedId === m.module_id ? 'tf-badge-gold font-semibold' : 'text-[color:var(--tf-slate)] hover:bg-[color:var(--tf-parchment)]'
            }`}
            data-testid={`tfvf-admin-mod-${m.module_id}`}
          >
            <span className="text-[10px] tf-eyebrow">{m.module_id}</span> · v{m.version}
            <p className="text-xs truncate">{m.title}</p>
          </button>
        ))}
      </aside>

      {/* Editor */}
      <section className="lg:col-span-3 space-y-4">
        {!mod ? (
          <div className="tf-card rounded-xl p-6 text-center text-[color:var(--tf-muted)]">Pick a module to edit</div>
        ) : (
          <>
            <div className="tf-card rounded-xl p-5 space-y-3" data-testid="tfvf-admin-editor">
              <div className="flex items-center justify-between">
                <p className="tf-eyebrow">Editing · {mod.module_id} · currently v{mod.version}</p>
                <button
                  onClick={save}
                  disabled={saving}
                  className="tf-btn-primary rounded-lg px-4 py-2 text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid="tfvf-admin-save-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save & bump version
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-[color:var(--tf-forest)]">Title</label>
                  <input value={mod.title || ''} onChange={e => setMod({ ...mod, title: e.target.value })} className="w-full mt-1 border border-[color:var(--tf-forest)]/15 rounded-lg px-3 py-2 text-sm bg-white" data-testid="tfvf-admin-title" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[color:var(--tf-forest)]">Audience</label>
                  <input value={mod.audience || ''} onChange={e => setMod({ ...mod, audience: e.target.value })} className="w-full mt-1 border border-[color:var(--tf-forest)]/15 rounded-lg px-3 py-2 text-sm bg-white" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[color:var(--tf-forest)]">Duration (min)</label>
                  <input type="number" value={mod.duration_minutes || 0} onChange={e => setMod({ ...mod, duration_minutes: Number(e.target.value) })} className="w-full mt-1 border border-[color:var(--tf-forest)]/15 rounded-lg px-3 py-2 text-sm bg-white" />
                </div>
                <div className="flex items-end">
                  <label className="text-sm flex items-center gap-2">
                    <input type="checkbox" checked={!!mod.required} onChange={e => setMod({ ...mod, required: e.target.checked })} className="accent-[color:var(--tf-gold)]" />
                    Required in learning path
                  </label>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-[color:var(--tf-forest)]">Summary</label>
                <textarea value={mod.summary || ''} onChange={e => setMod({ ...mod, summary: e.target.value })} rows={2} className="w-full mt-1 border border-[color:var(--tf-forest)]/15 rounded-lg px-3 py-2 text-sm bg-white" />
              </div>
            </div>

            {/* Content chunks */}
            <div className="tf-card rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="tf-eyebrow">In-platform content sections</p>
                <button onClick={addChunk} className="text-xs text-[color:var(--tf-gold-2)] hover:underline inline-flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Add section
                </button>
              </div>
              <div className="space-y-3">
                {mod.content_chunks.map((c, i) => (
                  <div key={i} className="border border-[color:var(--tf-forest)]/10 rounded-lg p-3" data-testid={`tfvf-admin-chunk-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-3 h-3 text-[color:var(--tf-muted)]" />
                      <input value={c.title} onChange={e => patchChunk(i, { title: e.target.value })} className="flex-1 font-semibold text-sm bg-transparent border-b border-transparent hover:border-[color:var(--tf-forest)]/20 focus:border-[color:var(--tf-gold)] focus:outline-none" />
                      <span className="text-[10px] text-[color:var(--tf-muted)] italic">{c.source_doc}</span>
                      <button onClick={() => removeChunk(i)} className="text-rose-600 hover:text-rose-800" aria-label="Delete section"><Trash2 className="w-3 h-3" /></button>
                    </div>
                    <textarea
                      value={c.body_markdown}
                      onChange={e => patchChunk(i, { body_markdown: e.target.value })}
                      rows={6}
                      className="w-full text-xs border border-[color:var(--tf-forest)]/10 rounded p-2 font-mono bg-[color:var(--tf-parchment)]/40 focus:outline-none focus:border-[color:var(--tf-gold)]"
                      placeholder="Markdown body — visible in the module page. Do not delete approved language from the reference sections."
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Audit for this module */}
            {filteredAudit.length > 0 && (
              <div className="tf-card rounded-xl p-5">
                <p className="tf-eyebrow mb-2">Version history</p>
                <div className="text-xs text-[color:var(--tf-slate)] space-y-1">
                  {filteredAudit.map((r, i) => (
                    <p key={i}>
                      <span className="font-mono">{new Date(r.at).toLocaleString()}</span>
                      {' — '}{r.action === 'edit_module' ? <>edited from v{r.from_version} → <b>v{r.to_version}</b> ({(r.fields || []).join(', ')})</> : r.action}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default TfvfAdmin;
